import { Component, Emit, Prop, Ref } from 'vue-property-decorator'
import valuableConceptsModalGridBasicForm from '@/components/valuable-concepts-modal/valuable-concepts-modal-grid-basic-form/valuable-concepts-modal-grid-basic-form.vue'
import valuableConceptsModalGridBasicFormComp from '@/components/valuable-concepts-modal/valuable-concepts-modal-grid-basic-form/valuable-concepts-modal-grid-basic-form'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridHeader from '@/components/GridHeader.vue'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { AppliedBillableConceptResponse, SumPerAccountingUnitResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { initialValuableConceptsGridModalState, ValuableConceptsModalGridState } from '@/components/valuable-concepts-modal/valuable-concepts-modal-grid/valuable-concepts-modal-grid.model'
import { GetContextMenuItemsParams, GridReadyEvent, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { ValuableConceptsTreeNode } from '@/components/valuable-concepts-modal/valuable-concepts-modal-tree/valuable-concepts-modal-tree.model'
import { CommonDatasourceValuableConceptsModal } from '@/components/valuable-concepts-modal/datasource/valuable-concepts-modal-datasource'
import { ValuableConceptsModalGridBasicFormInfo } from '@/components/valuable-concepts-modal/valuable-concepts-modal-grid-basic-form/valuable-concepts-modal-grid-basic-form.model'
import { CommonExportType, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AxiosResponse } from 'axios'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { GridUtils } from '@/shared/util/grid-utils'
import { getFilterModelByEntity } from '@/components/valuable-concepts-modal/shared/helpers/helpers'
import { ReportGridRequest } from '@/api/services/common.dto'
import { CASE_STATES } from '@/api/resources/entityState/case-state'

@Component({
    components: {
        VtGrid,
        GridHeader,
        valuableConceptsModalGridBasicForm
    }
})
export default class ValuableConceptsModalGrid extends BaseCrudServerSideComponent<AppliedBillableConceptResponse> implements BaseCrud {
    @Ref() readonly valuableConceptsModalGridBasicFormWrapper!: valuableConceptsModalGridBasicFormComp
    @Emit()
    refreshView(): void {}

    state: ValuableConceptsModalGridState = initialValuableConceptsGridModalState()

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1 && !this.isLoading
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length && !this.isLoading
    }

    get selectedNode(): StrictNullable<ValuableConceptsTreeNode> {
        return this.state.selectedNode
    }

    get selectedNodeLabel(): StrictNullable<string> {
        return this.state.selectedNode?.fullLabel || null
    }

    get selectedNodeEntityLabel(): StrictNullable<string> {
        return this.state.selectedNode?.entityTypeLabel || null
    }

    get isLoading(): boolean {
        return this.state.loading
    }

    get canAddOrUpdateOrDeleteConcepts(): boolean {
        const disabledCaseStates = [CASE_STATES.VALIDATED.code, CASE_STATES.IN_AMENDMENT.code]
        let canToAddOrUpdateOrDeleteAction
        if (this.state.caseStatus) {
            canToAddOrUpdateOrDeleteAction = !disabledCaseStates.includes(this.state.caseStatus)
        } else {
            canToAddOrUpdateOrDeleteAction = true
        }
        return this.state.isBillingModule || (!this.state.isBillingModule && canToAddOrUpdateOrDeleteAction)
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAppliedBillableConcepts(requestDelete)) {
                    this.refreshView()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///
    onUpdate(appliedBillableConcept: AppliedBillableConceptResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        const info: ValuableConceptsModalGridBasicFormInfo = {
            caseId: this.state.caseId,
            nodeSelected: this.state.selectedNode,
            rowSelected: appliedBillableConcept,
            accountingUnit: this.state.accountingUnit,
            isBillingModule: this.state.isBillingModule
        }
        if (appliedBillableConcept) {
            this.showModal(
                this.$tc('configuration.actions.update'),
                info,
                this.valuableConceptsModalGridBasicFormWrapper
            )
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(appliedBillableConcept => appliedBillableConcept.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        const info: ValuableConceptsModalGridBasicFormInfo = {
            caseId: this.state.caseId,
            nodeSelected: this.state.selectedNode,
            rowSelected: null,
            accountingUnit: this.state.accountingUnit,
            isBillingModule: this.state.isBillingModule
        }
        this.showModal(
            this.$tc('configuration.actions.add'),
            info,
            this.valuableConceptsModalGridBasicFormWrapper
        )
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: !this.canUpdate || !this.canAddOrUpdateOrDeleteConcepts ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: !this.canDelete || !this.canAddOrUpdateOrDeleteConcepts || this._isCopaymentConceptRowNode(params?.node?.data) ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }

    onGridReady(event: GridReadyEvent): void {
        this.state.gridApi = event.api
        this.state.columnApi = event.columnApi

        if (this.state.columnsDef) {
            this.state.gridApi.setColumnDefs(this.state.columnsDef)

            if (this.state.filterModel) {
                this.state.gridApi.setFilterModel(this.state.filterModel)
            }
        }
    }

    onEndCallback(): void {
        this.state.loading = false
        this.state.gridApi?.forEachNode((item) => {
            const sameId = item.data.entityId === this.state.selectedNode?.id
            const sameEntity = item.data.entityType.code === this.state.selectedNode?.entityType
            if (sameId && sameEntity) {
                item.setSelected(true)
            }
        })
    }

    updateDatasource(node: ValuableConceptsTreeNode, accountingUnit: SumPerAccountingUnitResponse, caseId: number, isBillingModule: boolean, caseStatus: StrictNullable<string>): void {
        this.state.loading = true
        this.state.gridApi?.deselectAll()

        const datasource =
            new CommonDatasourceValuableConceptsModal(
                this.state.service,
                this.state.laboratoryId,
                node.entityType,
                node.id,
                accountingUnit.accountingUnit.id)
        this.state = {
            ...this.state,
            accountingUnit,
            selectedNode: node,
            datasource,
            caseId,
            isBillingModule,
            caseStatus: caseStatus ?? null
        }
        this.state.gridApi?.setServerSideDatasource(datasource)
    }

    onReloadView(): void {
        this.state.actionTitle = this.$tc('configuration.actions.update')
        this.refreshView()
    }

    async exportReport(exportType: CommonExportType): Promise<StrictNullable<AxiosResponse<Blob>>> {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi && this.state.selectedNode && this.state.accountingUnit) {
            const selectedIds = this.state.selectedRows.map(selectedRow => selectedRow.id)
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: selectedIds
            }
            const filterModel =
                getFilterModelByEntity(
                    this.state.gridApi.getFilterModel(),
                    this.state.selectedNode.entityType,
                    this.state.accountingUnit.accountingUnit.id,
                    this.state.selectedNode.id)
            const request: ReportGridRequest = {
                sectionLabel: `${this.selectedNodeLabel} - ${this.selectedNodeEntityLabel}`,
                columns,
                entityIdentifiersRequest,
                filterModel
            }
            const params: ExportCommonParams = {
                sort: GridUtils.getSortValue(this.state.gridApi.getSortModel()),
                exportType
            }
            return billingAppliedBillableConceptService.export(this.$store.getters.labId, request, params)
        }
        return null
    }

    private async _deleteAppliedBillableConcepts(requestDelete: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///
    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    private _isCopaymentConceptRowNode(rowNodeData: AppliedBillableConceptResponse): boolean {
        return rowNodeData.isCopayment
    }
}
